"use client";

// ============================================================
// CLUBHUB – Aufgaben als Brett: Offen · In Arbeit · Erledigt
// ============================================================
// Gestalt und Mechanik wie das Aufgabenbrett im Agent
// (`aufgaben/kanban-board.tsx`): ziehen mit der Maus, die Pfeile auf
// der Karte fuer Telefon und Tastatur, Ueberfaelliges mit rotem Rand.
// Auf dem Telefon stehen die Spalten untereinander — drei Spalten
// nebeneinander waeren dort je 100 Pixel breit.
// „Erledigt“ zeigt die letzten acht (das zuletzt Erledigte oben —
// der Bestand legt es dort ab); der Rest klappt auf.
//
// Wohin eine Karte faellt, bestimmt die Hoehe des Zeigers in der
// Spalte, nicht nur die Karte darunter: in den Luecken zwischen den
// Karten und ueber dem Spaltenkopf zielte sie sonst ans Ende.
// ============================================================
import { useState } from "react";
import { ArrowLeft, ArrowRight, CalendarDays, Check, Plus } from "lucide-react";
import type { TaskStatus } from "@/generated/prisma/enums";
import { Avatar, Button, toast } from "@/components/ui";
import { Ablegelinie, useZiehen } from "@/components/ui/ziehen";
import { TASK_STATUS } from "@/lib/labels";
import { STATUS_FOLGE, faelligText, istUeberfaellig } from "@/lib/pinnwand/logik";
import { istFehler } from "@/lib/action-result";
import type { AufgabeView } from "@/lib/pinnwand/views";
import { cn } from "@/lib/utils";
import { usePinnwand } from "./pinnwand-bestand";

const GAP = 12;
const ERLEDIGT_GEZEIGT = 8;

export function AufgabenBrett({
  aufgaben,
  onOeffnen,
  onNeu,
}: {
  aufgaben: AufgabeView[];
  onOeffnen: (a: AufgabeView) => void;
  onNeu: (status: TaskStatus) => void;
}) {
  const { rechte, aufgabeAblegen } = usePinnwand();
  const drag = useZiehen<"aufgabe">();

  function ablegen(status: TaskStatus, zielId?: string, position: "vor" | "nach" = "vor") {
    const held = drag.gezogen.current;
    drag.loslassen();
    if (!held || held.id === zielId) return;
    const spalte = aufgaben.filter((a) => a.status === status && a.id !== held.id);
    let vorId: string | undefined;
    if (zielId) {
      const i = spalte.findIndex((a) => a.id === zielId);
      vorId = position === "vor" ? zielId : spalte[i + 1]?.id;
    }
    const vorher = aufgaben.find((a) => a.id === held.id);
    void aufgabeAblegen(held.id, status, vorId).then((r) => {
      if (istFehler(r)) toast.error(r.error);
      else if (vorher && vorher.status !== status) toast.success(`${TASK_STATUS[status].label}: ${vorher.title}`);
    });
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {STATUS_FOLGE.map((status) => (
        <Spalte
          key={status}
          status={status}
          karten={aufgaben.filter((a) => a.status === status)}
          drag={drag}
          beweglich={rechte.schreiben}
          onAblegen={ablegen}
          onOeffnen={onOeffnen}
          onNeu={rechte.schreiben && status !== "DONE" ? () => onNeu(status) : undefined}
        />
      ))}
    </div>
  );
}

type Drag = ReturnType<typeof useZiehen<"aufgabe">>;

function Spalte({
  status,
  karten,
  drag,
  beweglich,
  onAblegen,
  onOeffnen,
  onNeu,
}: {
  status: TaskStatus;
  karten: AufgabeView[];
  drag: Drag;
  beweglich: boolean;
  onAblegen: (status: TaskStatus, zielId?: string, position?: "vor" | "nach") => void;
  onOeffnen: (a: AufgabeView) => void;
  onNeu?: () => void;
}) {
  const [alle, setAlle] = useState(false);
  const gekuerzt = status === "DONE" && !alle && karten.length > ERLEDIGT_GEZEIGT;
  const gezeigt = gekuerzt ? karten.slice(0, ERLEDIGT_GEZEIGT) : karten;

  /** Die Karte, vor die (oder hinter die letzte) der Zeiger gerade zielt. */
  function zielAmZeiger(e: React.DragEvent<HTMLElement>): { id: string; position: "vor" | "nach" } | null {
    const kandidaten = [...e.currentTarget.querySelectorAll<HTMLElement>("[data-karte]")].filter(
      (el) => el.dataset.karte !== drag.aktivId,
    );
    if (kandidaten.length === 0) return null;
    for (const el of kandidaten) {
      const r = el.getBoundingClientRect();
      if (e.clientY < r.top + r.height / 2) return { id: el.dataset.karte!, position: "vor" };
    }
    return { id: kandidaten[kandidaten.length - 1]!.dataset.karte!, position: "nach" };
  }

  return (
    <section
      aria-label={TASK_STATUS[status].label}
      onDragOver={(e) => {
        if (!drag.aktivId) return;
        e.preventDefault();
        const ziel = zielAmZeiger(e);
        drag.zielSetzen(ziel ? ziel.id : `leer:${status}`, ziel ? ziel.position : "vor");
      }}
      onDrop={(e) => {
        e.preventDefault();
        const ziel = zielAmZeiger(e);
        onAblegen(status, ziel?.id, ziel?.position ?? "nach");
      }}
      className="bg-bg-tertiary/50 min-w-0 space-y-3 rounded-lg p-3"
    >
      <h3 className="text-overline text-text-tertiary flex items-center justify-between px-1">
        <span>{TASK_STATUS[status].label}</span>
        <span className="numeric">{karten.length}</span>
      </h3>

      {gezeigt.map((a) => (
        <Karte
          key={a.id}
          aufgabe={a}
          beweglich={beweglich}
          markiert={drag.markiert(a.id)}
          gehalten={drag.aktivId === a.id}
          onGreifen={() => drag.anfassen({ art: "aufgabe", id: a.id, ausId: status })}
          onLoslassen={() => drag.loslassen()}
          onOeffnen={() => onOeffnen(a)}
        />
      ))}

      {karten.length === 0 && (
        <div className="relative">
          <Ablegelinie sichtbar={drag.markiert(`leer:${status}`) !== null} position="vor" luecke={GAP} />
          <p className="text-caption text-text-tertiary px-1 py-2">
            {status === "OPEN" ? "Nichts offen." : status === "IN_PROGRESS" ? "Gerade arbeitet niemand an etwas." : "Noch nichts erledigt."}
          </p>
        </div>
      )}

      {gekuerzt && (
        <button type="button" onClick={() => setAlle(true)} className="text-caption text-text-secondary hover:text-text-primary w-full px-1 text-left">
          {karten.length - ERLEDIGT_GEZEIGT} weitere erledigte zeigen
        </button>
      )}

      {onNeu && (
        <Button variant="ghost" size="sm" className="w-full" onClick={onNeu}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Aufgabe
        </Button>
      )}
    </section>
  );
}

function Karte({
  aufgabe: a,
  beweglich,
  markiert,
  gehalten,
  onGreifen,
  onLoslassen,
  onOeffnen,
}: {
  aufgabe: AufgabeView;
  beweglich: boolean;
  markiert: "vor" | "nach" | null;
  gehalten: boolean;
  onGreifen: () => void;
  onLoslassen: () => void;
  onOeffnen: () => void;
}) {
  const { personen, heute, aufgabeStatus } = usePinnwand();
  const p = a.assigneeId ? personen[a.assigneeId] : undefined;
  const ueber = istUeberfaellig(a, heute);
  const faellig = faelligText(a, heute);
  const i = STATUS_FOLGE.indexOf(a.status);

  async function schieben(nach: TaskStatus) {
    const r = await aufgabeStatus(a.id, nach);
    if (istFehler(r)) {
      toast.error(r.error);
      return;
    }
    toast.success(`${TASK_STATUS[nach].label}: ${a.title}`, {
      action: {
        label: "Rückgängig",
        onClick: () =>
          void aufgabeStatus(a.id, a.status).then((z) => {
            if (istFehler(z)) toast.error(z.error);
          }),
      },
    });
  }

  return (
    <div
      data-karte={a.id}
      draggable={beweglich}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", a.id);
        onGreifen();
      }}
      onDragEnd={onLoslassen}
      className={cn(
        "bg-bg-elevated shadow-e1 duration-fast ease-standard hover:shadow-e2 relative space-y-2 rounded-xl p-3 transition-shadow select-none",
        beweglich && "cursor-grab active:cursor-grabbing",
        gehalten && "opacity-40",
        ueber ? "ring-danger/40 ring-1 ring-inset" : "ring-1 ring-black/[0.04] ring-inset dark:ring-white/[0.06]",
      )}
    >
      <Ablegelinie sichtbar={markiert !== null} position={markiert ?? "vor"} luecke={GAP} />

      <button
        type="button"
        onClick={onOeffnen}
        className={cn(
          "text-body-sm hover:text-accent block w-full text-left leading-tight font-medium break-words hyphens-auto transition-colors",
          a.status === "DONE" ? "text-text-secondary" : "text-text-primary",
        )}
      >
        {a.title}
      </button>

      {a.description && <p className="text-caption text-text-tertiary line-clamp-2">{a.description}</p>}

      {(faellig || a.eventTitle) && (
        <div className="text-caption flex flex-wrap items-center gap-x-3 gap-y-1">
          {faellig && <span className={cn("numeric", ueber ? "text-danger font-medium" : "text-text-tertiary")}>{faellig}</span>}
          {a.eventTitle && (
            <span className="text-text-tertiary inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" aria-hidden />
              {a.eventTitle}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-caption text-text-secondary flex min-w-0 items-center gap-1.5">
          {p ? (
            <>
              <Avatar firstName={p.firstName} lastName={p.lastName} size="sm" className="ring-bg-elevated h-5 w-5 text-[9px] ring-2" hint={false} />
              <span className="truncate">{p.firstName} {p.lastName}</span>
            </>
          ) : (
            <span className="text-text-tertiary">Niemand zuständig</span>
          )}
        </span>
        {beweglich && (
          <span className="flex shrink-0 items-center">
            {i > 0 && (
              <button type="button" onClick={() => void schieben(STATUS_FOLGE[i - 1]!)} title="Eine Spalte zurück" aria-label="Eine Spalte zurück" className="text-text-tertiary hover:text-text-primary grid h-7 w-7 place-items-center rounded-md">
                <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
            {i < STATUS_FOLGE.length - 1 && (
              <button
                type="button"
                onClick={() => void schieben(STATUS_FOLGE[i + 1]!)}
                title={i === STATUS_FOLGE.length - 2 ? "Erledigt" : "Eine Spalte weiter"}
                aria-label={i === STATUS_FOLGE.length - 2 ? "Als erledigt markieren" : "Eine Spalte weiter"}
                className="text-text-tertiary hover:text-text-primary grid h-7 w-7 place-items-center rounded-md"
              >
                {i === STATUS_FOLGE.length - 2 ? <Check className="h-3.5 w-3.5" aria-hidden /> : <ArrowRight className="h-3.5 w-3.5" aria-hidden />}
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}
